import { getAllTagsFn } from "@/actions/tags/handler";
import { trackContentSearch } from "@/components/analytics/conversions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { forumCategoryLabels } from "@/utils/forum-catalog";
import { seo } from "@/utils/seo";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ArrowRight,
  Edit,
  Eye,
  MessageSquare,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_app/forum/")({
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery({
      queryKey: ["tags"],
      queryFn: getAllTagsFn,
    });
  },
  head: () => ({
    meta: seo({
      title: "Forum | theCoach",
      description:
        "Powerlifting, fitness, vücut geliştirme ve antrenman üzerine toplulukla etkileşime geçin. Soru sorun, bilgi paylaşın ve spor dünyasına katılın.",
      keywords:
        "powerlifting, fitness, vücut geliştirme, spor forumu, antrenman, beslenme, supplement, sporcu sağlığı",
      canonical: "https://atalaythecoach.com/forum",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Forum | theCoach",
          description: "Sporcular için tartışma ve bilgi paylaşım platformu.",
          url: "https://atalaythecoach.com/forum",
          publisher: {
            "@type": "Organization",
            name: "theCoach",
            logo: {
              "@type": "ImageObject",
              url: "https://atalaythecoach.com/favicon/favicon.svg",
            },
          },
        }),
      },
    ],
  }),
  component: ForumIndexPage,
});

function ForumIndexPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: getAllTagsFn,
  });
  const { user } = useRouteContext({ from: "/_app" });
  const navigate = useNavigate();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<
    "new" | "unanswered" | "expert" | "popular"
  >("new");
  const topics = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("tr-TR");
    const list = (data?.body?.tags || []).filter((topic) => {
      if (
        normalized &&
        !`${topic.tagTitle} ${topic.tagContent} ${topic.category}`
          .toLocaleLowerCase("tr-TR")
          .includes(normalized)
      )
        return false;
      if (filter === "unanswered") return topic.messageCount === 0;
      if (filter === "expert") return topic.hasExpertAnswer;
      return true;
    });
    if (filter === "popular") {
      return [...list].sort(
        (a, b) =>
          b.viewCount + b.messageCount * 5 - (a.viewCount + a.messageCount * 5),
      );
    }
    return list;
  }, [data?.body?.tags, filter, query]);
  const allTopics = data?.body?.tags || [];
  const expertCount = allTopics.filter((topic) => topic.hasExpertAnswer).length;
  const unansweredCount = allTopics.filter(
    (topic) => topic.messageCount === 0,
  ).length;

  const handleCreateTopicClick = () => {
    if (user) {
      navigate({ to: "/forum/create" });
    } else {
      setShowLoginPopup(true);
    }
  };

  return (
    <>
      <div className="min-h-screen w-full bg-background pt-28 pb-16 text-foreground sm:pt-32">
        <div className="container mx-auto max-w-7xl px-4">
          <header className="mb-8 flex flex-col items-start justify-between sm:mb-12 sm:flex-row sm:items-center">
            <div>
              <h1 className="font-extrabold font-serif text-4xl tracking-tight sm:text-5xl">
                Forum
              </h1>
              <p className="mt-2 text-lg text-muted-foreground">
                Antrenman sorunuzu sorun; topluluk deneyimi ve uzman
                yanıtlarıyla çözüm bulun.
              </p>
            </div>
            <Button onClick={handleCreateTopicClick}>
              <Edit className="mr-2 h-4 w-4" />
              Yeni Konu Oluştur
            </Button>
          </header>

          <div className="mb-7 grid gap-3 rounded-xl border bg-muted/20 p-3 md:grid-cols-[1fr_auto]">
            <div className="relative">
              <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && query.trim()) {
                    trackContentSearch("forum", query.trim());
                  }
                }}
                placeholder="Sorularda ara"
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["new", "Yeni"],
                  ["unanswered", "Yanıtsız"],
                  ["expert", "Uzman yanıtlı"],
                  ["popular", "Popüler"],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={filter === value ? "default" : "outline"}
                  onClick={() => setFilter(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          <section className="mb-8 grid gap-3 sm:grid-cols-3">
            <Stat label="Yayındaki soru" value={allTopics.length} />
            <Stat label="Uzman yanıtlı" value={expertCount} />
            <Stat label="Yanıt bekleyen" value={unansweredCount} />
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            {isLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <Card key={index}>
                    <CardContent className="space-y-4 p-5">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-6 w-4/5" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </CardContent>
                  </Card>
                ))
              : topics.map((topic) => (
                  <Card
                    key={topic.tagId}
                    className="group flex h-full flex-col transition-colors hover:border-primary/60"
                  >
                    <CardContent className="flex h-full flex-col p-5 sm:p-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                          {forumCategoryLabels[topic.category]}
                        </Badge>
                        {topic.hasExpertAnswer && (
                          <Badge className="bg-blue-600">
                            <ShieldCheck className="mr-1 h-3 w-3" />
                            Uzman yanıtı
                          </Badge>
                        )}
                        {topic.topics?.slice(0, 2).map((item) => (
                          <Badge key={item} variant="secondary">
                            {item}
                          </Badge>
                        ))}
                      </div>
                      <Link
                        to="/forum/$tagId"
                        params={{ tagId: topic.slug || topic.tagId }}
                        className="mt-4 font-bold text-xl leading-snug transition-colors group-hover:text-primary"
                      >
                        {topic.tagTitle}
                      </Link>
                      <p className="mt-3 line-clamp-2 flex-1 text-muted-foreground text-sm leading-6">
                        {topic.tagContent}
                      </p>
                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-muted-foreground text-xs">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={topic.creater?.image || ""} />
                            <AvatarFallback>
                              {topic.creater?.userName?.[0]?.toUpperCase() ||
                                "K"}
                            </AvatarFallback>
                          </Avatar>
                          <span>{topic.creater?.userName}</span>
                          <span className="inline-flex items-center gap-1">
                            <MessageSquare className="h-3.5 w-3.5" />
                            {topic.messageCount}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Eye className="h-3.5 w-3.5" />
                            {topic.viewCount}
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-primary" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
          </section>
          {!isLoading && topics.length === 0 && (
            <div className="rounded-2xl border border-dashed p-10 text-center">
              <h2 className="font-semibold text-xl">Soru bulunamadı</h2>
              <p className="mt-2 text-muted-foreground">
                Aramayı veya seçili filtreyi değiştirerek tekrar deneyin.
              </p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showLoginPopup} onOpenChange={setShowLoginPopup}>
        <AlertDialogContent className="text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Giriş Gerekli</AlertDialogTitle>
            <AlertDialogDescription>
              Yeni bir konu oluşturabilmek için lütfen hesabınıza giriş yapın.
              Hesabınız yoksa kolayca oluşturabilirsiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                asChild
                variant="default"
                onClick={() => setShowLoginPopup(false)}
              >
                <Link to="/password/login">Giriş Yap</Link>
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <strong className="text-2xl">{value}</strong>
      <p className="text-muted-foreground text-xs">{label}</p>
    </div>
  );
}
